import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { AuthorizationDialog } from '@/components/ui/authorization-dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Eye, Search, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/use-permissions';
import { useAutoRefresh } from '@/hooks/use-auto-refresh';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function VendorData({ user }) {
  const navigate = useNavigate();
  const { canCreate, canEdit, canDelete } = usePermissions(user);
  const [vendors, setVendors] = useState([]);
  const [filteredVendors, setFilteredVendors] = useState([]);
  const [allCities, setAllCities] = useState([]);
  const [pakistaniCities, setPakistaniCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [localImportedFilter, setLocalImportedFilter] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [open, setOpen] = useState(false);
  const [editVendor, setEditVendor] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const [formData, setFormData] = useState({
    vendor_name: '', phone_number: '', alt_number: '', address: '', local_or_imported: 'Local', vendor_type: '', city: '', country: 'Pakistan', wechat_qr_code: ''
  });
  const [showCustomCity, setShowCustomCity] = useState(false);
  const [customCity, setCustomCity] = useState('');
  const [qrCodeFile, setQrCodeFile] = useState(null);
  const [qrCodePreview, setQrCodePreview] = useState('');
  const [isProcessingQR, setIsProcessingQR] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [vendorToDelete, setVendorToDelete] = useState(null);

  useEffect(() => {
    fetchVendors();
    fetchCitiesAndCountries();
  }, []);

  useEffect(() => {
    let filtered = vendors;

    // Filter by search query (vendor name)
    if (searchQuery.trim() !== '') {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(vendor =>
        vendor.vendor_name.toLowerCase().includes(query)
      );
    }

    // Filter by Local/Imported
    if (localImportedFilter.trim() !== '') {
      filtered = filtered.filter(vendor => 
        vendor.local_or_imported === localImportedFilter
      );
    }

    // Filter by City
    if (cityFilter.trim() !== '') {
      const query = cityFilter.toLowerCase();
      filtered = filtered.filter(vendor => 
        vendor.city.toLowerCase().includes(query)
      );
    }

    // Filter by Country
    if (countryFilter.trim() !== '') {
      const query = countryFilter.toLowerCase();
      filtered = filtered.filter(vendor => 
        vendor.country.toLowerCase().includes(query)
      );
    }

    setFilteredVendors(filtered);
  }, [searchQuery, localImportedFilter, cityFilter, countryFilter, vendors]);

  const handleBarcodeSearch = (e) => {
    if (e.key === 'Enter' && searchQuery.trim() !== '') {
      e.preventDefault();
      const query = searchQuery.toLowerCase();
      
      const found = vendors.find(vendor => {
        if (vendor.vendor_name.toLowerCase() === query) return true;
        return vendor.vendor_name.toLowerCase().includes(query);
      });
      
      if (found) {
        setViewDetails(found);
        toast.success('Vendor found! Opening details...');
      } else {
        toast.error('No vendor found');
      }
    }
  };

  const fetchCitiesAndCountries = async () => {
    try {
      const token = localStorage.getItem('token');
      const [allCitiesRes, pakistaniCitiesRes, countriesRes] = await Promise.all([
        axios.get(`${API}/cities`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/pakistani-cities`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/countries`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setAllCities(allCitiesRes.data.cities);
      setPakistaniCities(pakistaniCitiesRes.data.cities);
      setCountries(countriesRes.data.countries);
    } catch (error) {
      console.error('Failed to fetch cities/countries');
    }
  };

  const fetchVendors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/vendors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendors(response.data);
      setFilteredVendors(response.data);
    } catch (error) {
      console.error('Failed to fetch vendors');
    }
  };

  // Auto-refresh every 5 seconds, but pause when user is editing
  const isUserEditing = open || !!editVendor;
  useAutoRefresh(fetchVendors, 5, isUserEditing);

  const handleCityChange = async (selectedCity) => {
    if (selectedCity === 'OTHER') {
      setShowCustomCity(true);
      setFormData({ ...formData, city: '', country: '' });
      return;
    }
    
    setShowCustomCity(false);
    setCustomCity('');
    setFormData({ ...formData, city: selectedCity, country: '' });
    
    if (selectedCity) {
      try {
        const response = await axios.get(`${API}/city-country/${encodeURIComponent(selectedCity)}`);
        setFormData(prev => ({ ...prev, city: selectedCity, country: response.data.country }));
      } catch (error) {
        console.log(`City ${selectedCity} not found in mapping, keeping country empty`);
        setFormData(prev => ({ ...prev, city: selectedCity, country: '' }));
      }
    }
  };

  const handleCustomCityChange = (value) => {
    setCustomCity(value);
    setFormData(prev => ({ ...prev, city: value, country: '' }));
  };

  const handleLocalImportedChange = (value) => {
    setShowCustomCity(false);
    setCustomCity('');
    if (value === 'Local') {
      setFormData({ ...formData, local_or_imported: value, city: '', country: 'Pakistan' });
    } else {
      setFormData({ ...formData, local_or_imported: value, city: '', country: '' });
    }
  };

  const processQRImage = async (file) => {
    setIsProcessingQR(true);
    try {
      const reader = new FileReader();
      const imageData = await new Promise((resolve) => {
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(file);
      });

      console.log('QR Image Upload:', {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        imageDataLength: imageData.length,
        preview: imageData.substring(0, 100)
      });

      // Directly use the uploaded image without backend processing
      setQrCodePreview(imageData);
      setFormData(prev => ({ ...prev, wechat_qr_code: imageData }));
      toast.success('QR code image uploaded successfully');
    } catch (error) {
      console.error('QR upload error:', error);
      toast.error('Failed to upload QR code image. Please try again.');
      setQrCodeFile(null);
      setQrCodePreview('');
    } finally {
      setIsProcessingQR(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const submitData = { ...formData };
      
      // Ensure we use the processed (cropped) QR code from preview
      if (qrCodePreview) {
        submitData.wechat_qr_code = qrCodePreview;
        console.log('Submitting CROPPED QR code:', {
          length: qrCodePreview.length,
          preview: qrCodePreview.substring(0, 100),
          source: 'qrCodePreview'
        });
      } else {
        // If preview is empty, user deleted the QR code
        submitData.wechat_qr_code = '';
        console.log('No QR code to submit (deleted or empty)');
      }
      
      if (editVendor) {
        await axios.put(`${API}/vendors/${editVendor.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/vendors`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditVendor(null);
      setShowCustomCity(false);
      setCustomCity('');
      setFormData({
        vendor_name: '', phone_number: '', alt_number: '', address: '', local_or_imported: 'Local', vendor_type: '', city: '', country: 'Pakistan', wechat_qr_code: ''
      });
      setQrCodeFile(null);
      setQrCodePreview('');
      fetchVendors();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Operation failed';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (vendor) => {
    setVendorToDelete(vendor);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!vendorToDelete) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/vendors/${vendorToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      setDeleteDialogOpen(false);
      setVendorToDelete(null);
      fetchVendors();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  const cancelDelete = () => {
    setDeleteDialogOpen(false);
    setVendorToDelete(null);
  };

  const getCityOptions = () => {
    return formData.local_or_imported === 'Local' ? pakistaniCities : allCities;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Vendor Data</h1>
              <p className="text-sm text-gray-600">Manage vendor information</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-6 space-y-4">
        {/* Main Search Bar */}
        <div className="flex gap-4 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search Vendor" 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} 
              onKeyDown={handleBarcodeSearch} 
              className="pl-10" 
            />
          </div>
          {canCreate('master_data') && (
            <Dialog open={open} onOpenChange={(isOpen) => { setOpen(isOpen); if (!isOpen) setErrorMessage(''); }}>
              <DialogTrigger asChild>
                <Button onClick={() => { 
                  setEditVendor(null);
                  setShowCustomCity(false);
                  setCustomCity('');
                  setFormData({ vendor_name: '', phone_number: '', alt_number: '', address: '', local_or_imported: 'Local', city: '', country: 'Pakistan', wechat_qr_code: '' });
                  setQrCodeFile(null);
                  setQrCodePreview('');
                  setErrorMessage(''); 
                }}>
                  <Plus className="w-4 h-4 mr-2" /> Add Vendor
                </Button>
              </DialogTrigger>
            </Dialog>
          )}
        </div>

        {/* Filter Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium">Type</Label>
            <SearchableSelect
              options={[
                { value: "", label: "All Types" },
                { value: "Local", label: "Local" },
                { value: "Imported", label: "Imported" }
              ]}
              value={localImportedFilter}
              onChange={(value) => setLocalImportedFilter(value)}
              placeholder="All Types"
              searchPlaceholder="Search type..."
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">City</Label>
            <Input
              placeholder="Search by city"
              value={cityFilter}
              onChange={(e) => setCityFilter(e.target.value)}
              className="text-sm"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Country</Label>
            <Input
              placeholder="Search by country"
              value={countryFilter}
              onChange={(e) => setCountryFilter(e.target.value)}
              className="text-sm"
            />
          </div>
          
          <div className="space-y-2">
            <Label className="text-sm font-medium">Actions</Label>
            <Button 
              variant="outline" 
              onClick={() => {
                setSearchQuery('');
                setLocalImportedFilter('');
                setCityFilter('');
                setCountryFilter('');
              }}
              className="w-full text-sm"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* Add/Edit Vendor Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
          <DialogHeader><DialogTitle>{editVendor ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle></DialogHeader>
          {errorMessage && (
            <div className="bg-red-50 border-2 border-red-500 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-bold text-red-800 mb-1">Error</h3>
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setErrorMessage('')}
                  className="ml-auto flex-shrink-0 inline-flex text-red-400 hover:text-red-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label><span className="text-red-500">*</span> Vendor Name</Label>
              <Input value={formData.vendor_name} onChange={(e) => setFormData({ ...formData, vendor_name: e.target.value })} required placeholder="Enter vendor name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone Number</Label>
                <Input value={formData.phone_number} onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })} placeholder="Enter phone number" />
              </div>
              <div className="space-y-2">
                <Label>Alt Number</Label>
                <Input value={formData.alt_number} onChange={(e) => setFormData({ ...formData, alt_number: e.target.value })} placeholder="Enter alternate number" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} placeholder="Enter vendor address" />
            </div>
            <div className="space-y-2">
              <Label><span className="text-red-500">*</span> Local or Imported</Label>
              <SearchableSelect
                options={[
                  { value: "Local", label: "Local" },
                  { value: "Imported", label: "Imported" }
                ]}
                value={formData.local_or_imported}
                onChange={(value) => handleLocalImportedChange(value)}
                placeholder="Select type"
                searchPlaceholder="Search type..."
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Vendor Type</Label>
              <SearchableSelect
                options={[
                  { value: "Manufacturer", label: "Manufacturer" },
                  { value: "Trader", label: "Trader" }
                ]}
                value={formData.vendor_type}
                onChange={(value) => setFormData({ ...formData, vendor_type: value })}
                placeholder="Select vendor type"
                searchPlaceholder="Search type..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label><span className="text-red-500">*</span> City</Label>
                {!showCustomCity ? (
                  <SearchableSelect
                    options={[
                      ...getCityOptions().map(city => ({ value: city, label: city })),
                      { value: "OTHER", label: "Other (Type your city)" }
                    ]}
                    value={formData.city}
                    onChange={(value) => handleCityChange(value)}
                    placeholder="Select City"
                    searchPlaceholder="Search city..."
                    required
                  />
                ) : (
                  <div className="space-y-2">
                    <Input 
                      value={customCity} 
                      onChange={(e) => handleCustomCityChange(e.target.value)} 
                      placeholder="Type city name"
                      required
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setShowCustomCity(false);
                        setCustomCity('');
                        setFormData(prev => ({ ...prev, city: '', country: formData.local_or_imported === 'Local' ? 'Pakistan' : '' }));
                      }}
                      className="w-full text-xs"
                    >
                      ← Back to city list
                    </Button>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Country</Label>
                <Input value={formData.country} readOnly className="bg-gray-100" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Upload WeChat QR Code</Label>
              <div className="flex items-center gap-3">
                <label 
                  htmlFor="qr-upload" 
                  className={`px-4 py-2 border-2 border-black rounded cursor-pointer hover:bg-gray-50 text-sm font-medium inline-block ${isProcessingQR ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {isProcessingQR ? 'Processing...' : 'Choose file'}
                </label>
                <input 
                  id="qr-upload"
                  type="file" 
                  accept="image/*"
                  className="hidden"
                  disabled={isProcessingQR}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setQrCodeFile(file);
                      processQRImage(file);
                    }
                  }}
                />
                {qrCodePreview && (
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => {
                      setQrCodeFile(null);
                      setQrCodePreview('');
                      setFormData({ ...formData, wechat_qr_code: '' });
                      document.getElementById('qr-upload').value = '';
                    }}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4 mr-1" /> Delete
                  </Button>
                )}
              </div>
              {qrCodePreview && (
                <div className="mt-2">
                  <img src={qrCodePreview} alt="QR Code Preview" className="w-32 h-32 object-contain border rounded" />
                </div>
              )}
            </div>
            <Button type="submit" className="w-full">{editVendor ? 'Update' : 'Create'} Vendor</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Vendor Cards Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {filteredVendors.map((vendor) => (
          <div key={vendor.id} className="bg-white border-2 border-black rounded-lg shadow p-4 flex flex-col items-center justify-between hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => setViewDetails(vendor)}>
            <h3 className="text-lg font-bold text-center mb-2">{vendor.vendor_name}</h3>
            <div className="text-center mb-3">
              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                vendor.local_or_imported === 'Local' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
              }`}>
                {vendor.local_or_imported} - {vendor.country}
              </span>
            </div>
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {canEdit('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => { 
                  setEditVendor(vendor); 
                  setFormData(vendor);
                  setQrCodeFile(null);
                  // Show existing QR code (file path or base64)
                  if (vendor.wechat_qr_code_path) {
                    setQrCodePreview(`${BACKEND_URL}/api/${vendor.wechat_qr_code_path}`);
                  } else if (vendor.wechat_qr_code) {
                    setQrCodePreview(vendor.wechat_qr_code);
                  } else {
                    setQrCodePreview('');
                  }
                  setOpen(true); 
                }}>
                  <Pencil className="w-4 h-4" />
                </Button>
              )}
              {canDelete('master_data') && (
                <Button variant="ghost" size="icon" onClick={() => handleDelete(vendor)}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredVendors.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">No vendors found.</p>
        </div>
      )}

      {/* View Details Dialog */}
      <Dialog open={!!viewDetails} onOpenChange={() => setViewDetails(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Vendor Details - {viewDetails?.vendor_name}</DialogTitle></DialogHeader>
          {viewDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><span className="font-semibold">Name:</span> {viewDetails.vendor_name}</div>
                <div>
                  <span className="font-semibold">Type: </span>
                  <span className={`px-2 py-1 rounded text-sm font-medium ${
                    viewDetails.local_or_imported === 'Local' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {viewDetails.local_or_imported}
                  </span>
                </div>
                <div><span className="font-semibold">Vendor Type:</span> {viewDetails.vendor_type || 'N/A'}</div>
                <div><span className="font-semibold">Phone:</span> {viewDetails.phone_number || 'N/A'}</div>
                <div><span className="font-semibold">Alt Number:</span> {viewDetails.alt_number || 'N/A'}</div>
                <div><span className="font-semibold">City:</span> {viewDetails.city}</div>
                <div><span className="font-semibold">Country:</span> {viewDetails.country}</div>
                <div className="col-span-2"><span className="font-semibold">Address:</span> {viewDetails.address || 'N/A'}</div>
              </div>
              {(viewDetails.wechat_qr_code_path || viewDetails.wechat_qr_code) && (
                <div className="mt-4 pt-4 border-t">
                  <p className="font-semibold mb-2">WeChat QR Code:</p>
                  <img 
                    src={viewDetails.wechat_qr_code_path ? `${BACKEND_URL}/api/${viewDetails.wechat_qr_code_path}` : viewDetails.wechat_qr_code} 
                    alt="WeChat QR Code" 
                    className="w-48 h-48 object-contain border rounded p-2"
                  />
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Authorization Dialog */}
      <AuthorizationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onAuthorized={confirmDelete}
        title="Delete Vendor"
        message={vendorToDelete ? `Enter authorization code to delete vendor ${vendorToDelete.vendor_name}` : 'Enter authorization code to proceed'}
      />
      </main>
    </div>
  );
}
